import { format as dateFnsFormat, isToday, isYesterday } from 'date-fns'
import { isNil } from 'lodash'
import { Duration } from 'luxon'

import { ValueOf } from 'modules/Monitoring/types'

import { pluralize } from './pluralize'

export const Formats = {
  Short: 'SHORT',
  Long: 'LONG',
  Tuple: 'TUPLE',
} as const

export type Format = ValueOf<typeof Formats>

export type DateUnsafe = Date | number | null | undefined

type DurationUnit =
  | 'years'
  | 'months'
  | 'weeks'
  | 'days'
  | 'hours'
  | 'minutes'
  | 'seconds'

type Options<T extends Format> = {
  format?: T
  smallestUnit?: DurationUnit
  largestUnit?: DurationUnit
  hideUnitForZero?: boolean
}
type TupleElement = [number, string]
type TupleReturnValue = TupleElement[]

const DISPLAY_UNITS_COUNT = 3
const DURATION_UNITS_ORDER: DurationUnit[] = [
  'years',
  'months',
  'weeks',
  'days',
  'hours',
  'minutes',
  'seconds',
]

const UNIT_FORMATTERS: Record<
  Format,
  Record<
    keyof Pick<
      Duration,
      'seconds' | 'minutes' | 'hours' | 'days' | 'weeks' | 'months' | 'years'
    >,
    (time: number) => string | [number, string]
  >
> = {
  [Formats.Short]: {
    seconds: seconds => `${seconds}s`,
    minutes: minutes => `${minutes}m`,
    hours: hours => `${hours}h`,
    days: days => `${days}d`,
    weeks: weeks => `${weeks}w`,
    months: months => `${months}mo`,
    years: years => `${years}y`,
  },
  [Formats.Long]: {
    seconds: seconds => pluralize('second', seconds),
    minutes: minutes => pluralize('minute', minutes),
    hours: hours => pluralize('hour', hours),
    days: days => pluralize('day', days),
    weeks: weeks => pluralize('week', weeks),
    months: months => pluralize('month', months),
    years: years => pluralize('year', years),
  },
  [Formats.Tuple]: {
    seconds: seconds => [seconds, 'sec'],
    minutes: minutes => [minutes, 'min'],
    hours: hours => [hours, 'hr'],
    days: days => [days, 'd'],
    weeks: weeks => [weeks, 'w'],
    months: months => [months, 'm'],
    years: years => [years, 'y'],
  },
}

export function formatDuration(
  duration: Duration | string | undefined,
  options?: Options<typeof Formats.Short | typeof Formats.Long>,
): string
export function formatDuration(
  duration: Duration | string | undefined,
  options?: Options<typeof Formats.Tuple>,
): TupleReturnValue
export function formatDuration(
  initialDuration: Duration | string | undefined,
  options?: Options<Format>,
): string | TupleReturnValue {
  const {
    format: dateFormat = Formats.Short,
    smallestUnit = 'minutes',
    largestUnit = 'days',
    hideUnitForZero = false,
  } = options ?? {}
  const isTuple = dateFormat === Formats.Tuple
  let duration = initialDuration

  if (typeof duration === 'string') {
    duration = convertSafeTimeToDuration(duration)
  }

  if (isNil(duration)) {
    return '-'
  }

  duration = normalizeDuration(duration as Duration, {
    largestUnit,
    smallestUnit,
  })

  const {
    years = 0,
    months = 0,
    weeks = 0,
    days = 0,
    hours = 0,
    minutes = 0,
    seconds = 0,
  } = duration.toObject()

  const formatters = UNIT_FORMATTERS[dateFormat]

  if (!(years || months || weeks || days || hours || minutes || seconds)) {
    const hideUnitForZeroValue = isTuple ? ([0, ''] as TupleElement) : '0'
    const value = hideUnitForZero ? hideUnitForZeroValue : formatters.seconds(0)

    return isTuple ? ([value] as TupleReturnValue) : (value as string)
  }

  const formattedUnits = [
    years && formatters.years(years),
    months && formatters.months(months),
    weeks && formatters.weeks(weeks),
    (days || months) && formatters.days(days ?? 0),
    hours && formatters.hours(hours),
    minutes && formatters.minutes(minutes),
    seconds && formatters.seconds(seconds),
  ]

  const largestNonEmptyUnit = formattedUnits.findIndex(entry => Boolean(entry))

  const units = formattedUnits
    .splice(largestNonEmptyUnit, DISPLAY_UNITS_COUNT)
    .filter(Boolean)

  if (isTuple) {
    return units as TupleReturnValue
  }

  return units.join(' ')
}

export const safeFormat = (date: DateUnsafe, formatString: string) => {
  if (!date) {
    return '-'
  }

  try {
    return dateFnsFormat(date, formatString)
  } catch (_) {
    return '-'
  }
}

export const convertSafeTimeToDuration = (date?: string | undefined) => {
  if (!date) {
    return undefined
  }

  try {
    const duration = Duration.fromISO(date)
    return duration.isValid ? duration : undefined
  } catch (_) {
    return undefined
  }
}

export const getFormattedDateTime = (date: number) => {
  if (isToday(date)) {
    return `Today, ${dateFnsFormat(date, 'HH:mm')}`
  }
  if (isYesterday(date)) {
    return `Yesterday, ${dateFnsFormat(date, 'HH:mm')}`
  }

  return dateFnsFormat(date, 'd LLL y, HH:mm')
}

function normalizeDuration(
  duration: Duration,
  {
    largestUnit,
    smallestUnit,
  }: {
    largestUnit: DurationUnit
    smallestUnit: DurationUnit
  },
): Duration {
  const unitRange = getUnitRange(largestUnit, smallestUnit)
  const shiftedDuration = duration.shiftTo(...unitRange)

  if (!shiftedDuration.isValid) {
    return duration
  }

  const roundedDuration = shiftedDuration.mapUnits((value, unit) => {
    if (unit === smallestUnit) {
      return Math.round(value)
    }

    return Math.trunc(value)
  })

  return roundedDuration.normalize()
}

function getUnitRange(
  largestUnit: DurationUnit,
  smallestUnit: DurationUnit,
): DurationUnit[] {
  const largestUnitIndex = DURATION_UNITS_ORDER.indexOf(largestUnit)
  const smallestUnitIndex = DURATION_UNITS_ORDER.indexOf(smallestUnit)

  if (
    largestUnitIndex < 0 ||
    smallestUnitIndex < 0 ||
    largestUnitIndex > smallestUnitIndex
  ) {
    return DURATION_UNITS_ORDER
  }

  return DURATION_UNITS_ORDER.slice(largestUnitIndex, smallestUnitIndex + 1)
}
